import { generateUuid } from "../../utils/id";
import type { BodyUUID, CameraUUID, SceneCamera, Vector3, Vector4 } from "../../types/scene";

type CameraParser = {
  cameraClassDefaults: Record<
    string,
    {
      pos?: number[];
      quat?: number[];
      euler?: number[];
      axisangle?: number[];
      xyaxes?: number[];
      zaxis?: number[];
    }
  >;
  classParentMap: Record<string, string>;
  coerceUuidFromMjcfName: (value: string | null) => string | null;
  parsePassthroughAttributes: (
    node: Element,
    skipNames?: string[]
  ) => Record<string, string> | undefined;
};

export const parseCameraNode = (
  parser: CameraParser,
  cameraElem: Element,
  defaults: { kind: "fixed" | "mounted"; mountBodyUuid?: BodyUUID }
): SceneCamera | null => {
  const name = cameraElem.getAttribute("name");
  if (!name) return null;
  const stableUuid = parser.coerceUuidFromMjcfName(name);
  const uuid = (stableUuid || generateUuid()) as CameraUUID;

  const parseAttrNums = (attr: string | null, expectedLen: number): number[] | null => {
    if (!attr) return null;
    const parsed = attr.trim().split(/\s+/).map(Number);
    return parsed.length === expectedLen && parsed.every((n) => Number.isFinite(n)) ? parsed : null;
  };
  const getInheritedCameraProp = (
    className: string | null,
    prop: "pos" | "quat" | "euler" | "axisangle" | "xyaxes" | "zaxis"
  ): number[] | undefined => {
    if (!className) return undefined;
    let cur: string | null = className;
    const visited = new Set<string>();
    while (cur && !visited.has(cur)) {
      visited.add(cur);
      const defaultsForClass = parser.cameraClassDefaults[cur];
      if (defaultsForClass && Array.isArray(defaultsForClass[prop])) {
        return defaultsForClass[prop];
      }
      cur = parser.classParentMap[cur] || null;
    }
    return undefined;
  };
  const quatMul = (a: Vector4, b: Vector4): Vector4 => {
    const [aw, ax, ay, az] = a;
    const [bw, bx, by, bz] = b;
    return [
      aw * bw - ax * bx - ay * by - az * bz,
      aw * bx + ax * bw + ay * bz - az * by,
      aw * by - ax * bz + ay * bw + az * bx,
      aw * bz + ax * by - ay * bx + az * bw,
    ];
  };
  const eulerDegToQuat = (eulerDeg: number[]): Vector4 => {
    const ex = ((eulerDeg[0] || 0) * Math.PI) / 180;
    const ey = ((eulerDeg[1] || 0) * Math.PI) / 180;
    const ez = ((eulerDeg[2] || 0) * Math.PI) / 180;
    const qx: Vector4 = [Math.cos(ex / 2), Math.sin(ex / 2), 0, 0];
    const qy: Vector4 = [Math.cos(ey / 2), 0, Math.sin(ey / 2), 0];
    const qz: Vector4 = [Math.cos(ez / 2), 0, 0, Math.sin(ez / 2)];
    return quatMul(quatMul(qx, qy), qz);
  };
  const axisAngleDegToQuat = (axisAngleDeg: number[]): Vector4 => {
    const ax = Number(axisAngleDeg[0]) || 0;
    const ay = Number(axisAngleDeg[1]) || 0;
    const az = Number(axisAngleDeg[2]) || 0;
    const angleRad = ((Number(axisAngleDeg[3]) || 0) * Math.PI) / 180;
    const axisMag = Math.hypot(ax, ay, az);
    if (axisMag < 1e-8) return [1, 0, 0, 0];
    const nx = ax / axisMag;
    const ny = ay / axisMag;
    const nz = az / axisMag;
    const s = Math.sin(angleRad / 2);
    return [Math.cos(angleRad / 2), nx * s, ny * s, nz * s];
  };
  const normalizeVec3 = (v: number[]): [number, number, number] | null => {
    if (!Array.isArray(v) || v.length !== 3) return null;
    const x = Number(v[0]) || 0;
    const y = Number(v[1]) || 0;
    const z = Number(v[2]) || 0;
    const mag = Math.hypot(x, y, z);
    if (mag < 1e-8) return null;
    return [x / mag, y / mag, z / mag];
  };
  const cross = (
    a: [number, number, number],
    b: [number, number, number]
  ): [number, number, number] => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
  const quatFromBasis = (
    xAxis: [number, number, number],
    yAxis: [number, number, number],
    zAxis: [number, number, number]
  ): Vector4 => {
    const m00 = xAxis[0],
      m10 = xAxis[1],
      m20 = xAxis[2];
    const m01 = yAxis[0],
      m11 = yAxis[1],
      m21 = yAxis[2];
    const m02 = zAxis[0],
      m12 = zAxis[1],
      m22 = zAxis[2];
    const trace = m00 + m11 + m22;
    if (trace > 0) {
      const s = Math.sqrt(trace + 1) * 2;
      return [0.25 * s, (m21 - m12) / s, (m02 - m20) / s, (m10 - m01) / s];
    }
    if (m00 > m11 && m00 > m22) {
      const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
      return [(m21 - m12) / s, 0.25 * s, (m01 + m10) / s, (m02 + m20) / s];
    }
    if (m11 > m22) {
      const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
      return [(m02 - m20) / s, (m01 + m10) / s, 0.25 * s, (m12 + m21) / s];
    }
    const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
    return [(m10 - m01) / s, (m02 + m20) / s, (m12 + m21) / s, 0.25 * s];
  };
  const xyaxesToQuat = (xyaxes: number[]): Vector4 | null => {
    if (!Array.isArray(xyaxes) || xyaxes.length !== 6) return null;
    const x = normalizeVec3([xyaxes[0], xyaxes[1], xyaxes[2]]);
    const yRaw = normalizeVec3([xyaxes[3], xyaxes[4], xyaxes[5]]);
    if (!x || !yRaw) return null;
    const z = normalizeVec3(cross(x, yRaw));
    if (!z) return null;
    const y = normalizeVec3(cross(z, x));
    if (!y) return null;
    return quatFromBasis(x, y, z);
  };
  const zaxisToQuat = (zaxis: number[]): Vector4 | null => {
    const z = normalizeVec3(zaxis);
    if (!z) return null;
    const worldUp: [number, number, number] = Math.abs(z[2]) > 0.999 ? [1, 0, 0] : [0, 0, 1];
    const x = normalizeVec3(cross(worldUp, z));
    if (!x) return null;
    const y = normalizeVec3(cross(z, x));
    if (!y) return null;
    return quatFromBasis(x, y, z);
  };

  const className = cameraElem.getAttribute("class");
  const passthroughAttrs =
    parser.parsePassthroughAttributes(cameraElem, [
      "name",
      "class",
      "pos",
      "quat",
      "euler",
      "axisangle",
      "xyaxes",
      "zaxis",
    ]) || undefined;
  const inheritedPos = getInheritedCameraProp(className, "pos");
  const explicitPos = parseAttrNums(cameraElem.getAttribute("pos"), 3);
  const posRaw = explicitPos || inheritedPos || [0, 0, 0];
  const pos = (
    Array.isArray(posRaw) && posRaw.length === 3 && posRaw.every((n) => Number.isFinite(n))
      ? posRaw
      : [0, 0, 0]
  ) as Vector3;

  const explicitQuat = parseAttrNums(cameraElem.getAttribute("quat"), 4);
  const inheritedQuat = getInheritedCameraProp(className, "quat");
  const explicitAxisAngle = parseAttrNums(cameraElem.getAttribute("axisangle"), 4);
  const inheritedAxisAngle = getInheritedCameraProp(className, "axisangle");
  const explicitEuler = parseAttrNums(cameraElem.getAttribute("euler"), 3);
  const inheritedEuler = getInheritedCameraProp(className, "euler");
  const explicitXyaxes = parseAttrNums(cameraElem.getAttribute("xyaxes"), 6);
  const inheritedXyaxes = getInheritedCameraProp(className, "xyaxes");
  const explicitZaxis = parseAttrNums(cameraElem.getAttribute("zaxis"), 3);
  const inheritedZaxis = getInheritedCameraProp(className, "zaxis");
  let quat = [1, 0, 0, 0] as Vector4;
  if (explicitQuat) {
    quat = explicitQuat as Vector4;
  } else if (inheritedQuat) {
    quat = inheritedQuat as Vector4;
  } else if (explicitAxisAngle) {
    quat = axisAngleDegToQuat(explicitAxisAngle);
  } else if (inheritedAxisAngle) {
    quat = axisAngleDegToQuat(inheritedAxisAngle);
  } else if (explicitEuler) {
    quat = eulerDegToQuat(explicitEuler);
  } else if (inheritedEuler) {
    quat = eulerDegToQuat(inheritedEuler);
  } else if (explicitXyaxes) {
    quat = xyaxesToQuat(explicitXyaxes) || quat;
  } else if (inheritedXyaxes) {
    quat = xyaxesToQuat(inheritedXyaxes) || quat;
  } else if (explicitZaxis) {
    quat = zaxisToQuat(explicitZaxis) || quat;
  } else if (inheritedZaxis) {
    quat = zaxisToQuat(inheritedZaxis) || quat;
  }

  const mag = Math.hypot(Number(quat[0]), Number(quat[1]), Number(quat[2]), Number(quat[3]));
  quat =
    mag > 1e-8
      ? ([quat[0] / mag, quat[1] / mag, quat[2] / mag, quat[3] / mag] as Vector4)
      : ([1, 0, 0, 0] as Vector4);
  const fovyRaw = Number(cameraElem.getAttribute("fovy"));
  const fovy = Number.isFinite(fovyRaw) ? Math.max(10, Math.min(150, fovyRaw)) : 65;
  const trackingModeRaw = cameraElem.getAttribute("mode");
  const trackingMode =
    typeof trackingModeRaw === "string" && trackingModeRaw.trim().length > 0
      ? trackingModeRaw.trim()
      : undefined;
  const targetBodyNameRaw =
    cameraElem.getAttribute("targetbody") || cameraElem.getAttribute("target");
  const targetBodyName =
    typeof targetBodyNameRaw === "string" && targetBodyNameRaw.trim().length > 0
      ? targetBodyNameRaw.trim()
      : undefined;
  // Inverse of export conversion for fixed cameras:
  // mujoco -> viewer vector: [x, y, z] -> [x, z, -y]
  // mujoco -> viewer quaternion: q_viewer = q_conv * q_mujoco, q_conv = Rx(-90deg)
  const qMujocoToViewer: Vector4 = [Math.SQRT1_2, -Math.SQRT1_2, 0, 0];
  const parsedPos: Vector3 =
    defaults.kind === "fixed"
      ? ([Number(pos[0]) || 0, Number(pos[2]) || 0, -(Number(pos[1]) || 0)] as Vector3)
      : pos;
  const parsedQuat: Vector4 =
    defaults.kind === "fixed" ? (quatMul(qMujocoToViewer, quat) as Vector4) : quat;
  return {
    uuid,
    name,
    kind: defaults.kind,
    position: parsedPos,
    orientation: parsedQuat,
    fovy,
    mountType: defaults.kind === "mounted" ? "body" : undefined,
    mountBodyUuid: defaults.kind === "mounted" ? defaults.mountBodyUuid : undefined,
    trackingMode,
    targetBodyName,
    ...(passthroughAttrs ? { attrs: passthroughAttrs } : {}),
  };
};
